import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { ErrorBoundary } from 'react-error-boundary'

import { useToggleStaticElements } from '../hooks/useToggleStaticElement'

type AnyElement = Element | Element[] | NodeListOf<Element>

interface PortalContextProps {
  /**
   * The root element this component will get rendered into
   */
  rootElement?: HTMLElement

  /**
   * Static (server rendered) element associated with this component.
   * Hide this element when the component gets rendered
   */
  staticElement?: HTMLElement

  toggleStaticElement: (isEnabled?: boolean) => void
}

export const PortalContext = React.createContext<PortalContextProps>({
  rootElement: undefined,
  staticElement: undefined,
  toggleStaticElement: () => {},
})

/**
 * Portal component props
 */
export interface PortalProps {
  /**
   * Override the ticker index from context if you need to skip the first ticker
   */
  index?: number

  /**
   * Server rendered elements to replace with react components
   */
  staticElements?: AnyElement | null

  /**
   * Immediately replace the static element with react children.
   * Turn this off if the child component is suspended
   *
   * @default true (if root exists)
   */
  toggleStaticElement?: boolean

  /**
   * Elements to render the react components into
   */
  roots: AnyElement | null

  /**
   * Intentionally restricted to JSX element so that we don’t have to check children is valid react element
   */
  children: JSX.Element
}

export function Portal({ index = 0, toggleStaticElement, staticElements, roots, children }: PortalProps) {
  // Get the root and associated static html element, get them by index if array
  const staticElement =
    staticElements instanceof NodeList || Array.isArray(staticElements) ? staticElements[index] : staticElements
  const rootElement = roots instanceof NodeList || Array.isArray(roots) ? roots[index] : roots

  /**
   * Set up element switching. We might want to hold off if we’re passing a suspendable component.
   * We can’t catch the promise here, it might have already been caught down the tree
   * and we want to avoid hiding the server-rendered element before the react content is ready
   */
  const [isEnabled, setIsEnabled] = React.useState<boolean>(toggleStaticElement ?? (!!staticElement && !!rootElement))
  useToggleStaticElements(staticElement, rootElement, isEnabled)

  const portalContextValue = React.useMemo(
    () => ({
      // Cast here to make it easier to pass any `Element`s as props
      rootElement: rootElement as HTMLElement,
      staticElement: staticElement as HTMLElement,
      // A way to get notified when children get rendered, because they could be suspended and waiting for requests
      toggleStaticElement: (enabled?: boolean) => setIsEnabled((prevValue) => enabled ?? !prevValue),
    }),
    [rootElement, staticElement]
  )

  if (!rootElement) return null

  return (
    <ErrorBoundary fallback={null} onError={() => setIsEnabled(false)}>
      <PortalContext.Provider value={portalContextValue}>
        {ReactDOM.createPortal(React.cloneElement(children, { staticElement, rootElement }), rootElement)}
      </PortalContext.Provider>
    </ErrorBoundary>
  )
}
